import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import linkadmin from './LinkAdmin.module.css';
import '@flaticon/flaticon-uicons/css/all/all.css';
import { ref, onValue, push, set, remove, update, get } from 'firebase/database';
import { auth, database } from '../../firebase';
import QRCode from 'react-qr-code';



const LinkAdmin = () => {
    const { pageId } = useParams();
    const [data, setData] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isPageOn, setIsPageOn] = useState(false);
    const [pin, setPin] = useState('');
    const [title, setTitle] = useState('Link Admin Page');
    const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const baseUrl = 'https://linksync-one.vercel.app';



    useEffect(() => {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            navigate('/login');
            return;
        }

        const pageRef = ref(database, `Pages/${pageId}`);
        onValue(pageRef, (snapshot) => {
            const pageData = snapshot.val();
            if (pageData) {
                setTitle(pageData.Name || 'Link Admin Page');
                setIsPageOn(pageData.published || false);
                setPin(pageData.pin || ''); // Add this line to set the pin from the page data
                if (pageData.Content) {
                    const contentArray = Object.keys(pageData.Content).map(key => ({
                        id: key,
                        ...pageData.Content[key]
                    }));
                    setData(contentArray);
                } else {
                    setData([]);
                }
            }
            setLoading(false);
        });

        if (pin) {
            const sessionRef = ref(database, `Sessions/${pin}`);
            onValue(sessionRef, (snapshot) => {
                const sessionData = snapshot.val();
                if (sessionData && sessionData.Content) {
                    const contentArray = Object.keys(sessionData.Content).map(key => ({
                        id: key,
                        ...sessionData.Content[key]
                    }));
                    setData(contentArray);
                }
            });
        }
    }, [pageId, navigate, pin]);

    if (loading) {
        return <div>Loading...</div>;
    }

    const handleOpen = (url) => {
        window.open(url, '_blank');
    };

    const handleCopy = (text) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                console.log('Text copied to clipboard');
            }).catch(err => {
                console.error('Failed to copy text: ', err);
            });
        } else {
            console.error('Clipboard API not supported');
            // Fallback: Use a textarea element to copy text
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                console.log('Text copied to clipboard');
            } catch (err) {
                console.error('Failed to copy text: ', err);
            }
            document.body.removeChild(textArea);
        }
    };
    const handleEdit = (item) => {
        setCurrentItem(item);
        setIsEditModalOpen(true);
    };

    const handleSave = async () => {
        // Validate Title
        if (!currentItem?.title) {
            alert('Title is required');
            return;
        }

        // Validate Content
        if (!currentItem?.content) {
            alert('Content is required');
            return;
        }

        // Proceed with the save operation if validation passes
        if (currentItem.id) {
            await update(ref(database, `Pages/${pageId}/Content/${currentItem.id}`), currentItem);
            setData(data.map(item => item.id === currentItem.id ? currentItem : item)); // Update state with edited content

            if (isPageOn && pin) {
                await update(ref(database, `Sessions/${pin}/Content/${currentItem.id}`), currentItem);
            }
        } else {
            const newContentRef = push(ref(database, `Pages/${pageId}/Content`));
            await set(newContentRef, currentItem);
            setData([...data, { ...currentItem, id: newContentRef.key }]); // Update state with new content

            if (isPageOn && pin) {
                await set(ref(database, `Sessions/${pin}/Content/${newContentRef.key}`), currentItem);
            }
        }

        // Close modals after successful save
        setIsEditModalOpen(false);
        setIsNewModalOpen(false);
    };

    const handleDelete = async (id) => {
        await remove(ref(database, `Pages/${pageId}/Content/${id}`));
        setData(data.filter(item => item.id !== id));

        if (isPageOn && pin) {
            await remove(ref(database, `Sessions/${pin}/Content/${id}`));
        }
        setIsEditModalOpen(false);
    };

    const handleAddNew = (type) => {
        setCurrentItem({ title: '', type });
        setIsNewModalOpen(true);
        setDropdownOpen(false);
    };


    const handleTogglePublish = async () => {
        const newStatus = !isPageOn;
        setIsPageOn(newStatus);
        if (!newStatus) {
            await update(ref(database, `Pages/${pageId}`), { published: false, pin: null, sessionID: null });
            await remove(ref(database, `Sessions/${pin}`)); // Remove the session when unpublishing
            setPin(''); // Clear the pin when unpublishing
        } else {
            await update(ref(database, `Pages/${pageId}`), { published: true });
            await generatePin(); // Generate pin when the page is published
        }
    };

    const formatPin = (pin) => {
        return pin ? `${pin.slice(0, 3)}-${pin.slice(3)}` : '';
    };

    const generatePin = async () => {
        let newPin;
        let pinExists = true;

        // Generate a unique pin
        while (pinExists) {
            newPin = Math.floor(100000 + Math.random() * 900000).toString();
            const pinRef = ref(database, `Pages/${pageId}`);
            const snapshot = await get(pinRef);
            pinExists = snapshot.exists() && snapshot.val().pin === newPin;
        }

        // Save the pin and session synchronously
        const updates = {
            pin: newPin,
            sessionID: newPin,
            [`/Sessions/${newPin}`]: {
                Name: title,
                Content: data.reduce((acc, item) => {
                    acc[item.id] = item;
                    return acc;
                }, {})
            }
        };

        await update(ref(database, `Pages/${pageId}`), { pin: newPin, sessionID: newPin });
        await update(ref(database, `/Sessions/${newPin}`), updates[`/Sessions/${newPin}`]);
        setPin(newPin);
    };

    const handleSavePin = async (pin) => {
        const formattedPin = pin.replace(/-/g, ''); // Remove dashes
        if (formattedPin.length === 6) {
            setPin(formattedPin);
            await update(ref(database, `Pages/${pageId}`), { pin: formattedPin });
        } else {
            alert('Pin must be 6 digits');
        }
    };

    const handleTitleEdit = () => {
        setIsTitleModalOpen(true);
    };

    const handleTitleSave = async () => {
        setIsTitleModalOpen(false);
        await update(ref(database, `Pages/${pageId}`), { Name: title });

        // Update the session title if the page is published
        if (isPageOn) {
            const sessionID = `session-${pin}`;
            await update(ref(database, `Sessions/${sessionID}`), { Name: title });
        }
    };


    return (
        <div className={linkadmin['link-page-container']}>
            <div className={linkadmin['title-card']}>
                <div className={linkadmin['left-title-card']}>
                    <div className={linkadmin['title-card-title']}>
                        <h2>{title}</h2>
                    </div>
                    <div className={linkadmin['pin-display']}>
                        {isPageOn && formatPin(pin)} {/* Add isPageOn check here */}
                        {isPageOn && pin && ( // Add isPageOn check here
                            <QRCode
                                value={`${baseUrl}/session/${pin}`}
                                size={50} // Adjust size as needed
                                className={linkadmin['qr-code']}
                            />
                        )}
                    </div>
                </div>
                <div className={linkadmin['title-card-actions']}>
                    <div className={linkadmin['toggle-container']}>
                    <span className={linkadmin['toggle-label']}>
                        {isPageOn ? 'Published' : 'Unpublished'}
                    </span>
                        <label className={linkadmin['switch']}>
                            <input type="checkbox" checked={isPageOn} onChange={handleTogglePublish}/>
                            <span className={linkadmin['slider']}></span>
                        </label>
                    </div>
                    <div className={linkadmin['button-group']}>
                        <button className={linkadmin['square-button']} onClick={handleTitleEdit} title="Edit Title">
                            <i className="fi fi-rr-edit"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div className={linkadmin['link-table']}>
                {data.map((item) => (
                    <div key={item.id} className={linkadmin['link-card']}>
                        <div className={linkadmin['link-title']}>{item.title}</div>
                        <div className={linkadmin['link-body']}>{item.content}</div>
                        <div className={linkadmin['link-actions']}>
                            {item.content.startsWith('http') ? (
                                <>
                                    <div className={linkadmin['square-button']} onClick={() => handleCopy(item.content)}
                                         title="Copy Link">
                                        <i className="fi fi-rr-copy-alt"></i>
                                    </div>
                                    <div className={linkadmin['square-button']} onClick={() => handleOpen(item.content)}
                                         title="Open Link">
                                        <i className="fi fi-sr-folder-open"></i>
                                    </div>
                                </>
                            ) : (
                                <div className={linkadmin['square-button']} onClick={() => handleCopy(item.content)}
                                     title="Copy">
                                    <i className="fi fi-rr-copy-alt"></i>
                                </div>
                            )}
                            <div className={linkadmin['square-button']} onClick={() => handleEdit(item)} title="Edit">
                                <i className="fi fi-rr-edit"></i>
                            </div>
                            <div className={linkadmin['square-button']} onClick={() => handleDelete(item.id)}
                                 title="Delete">
                                <i className="fi fi-rr-trash"></i>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className={linkadmin['dropdown']}>
                <button className={linkadmin['dropdown-button']} onClick={() => setDropdownOpen(!dropdownOpen)}>
                    <i className="fi fi-rr-plus"></i> Add
                </button>
                {dropdownOpen && (
                    <div className={linkadmin['dropdown-content']}>
                        <div className={linkadmin['dropdown-item']} onClick={() => handleAddNew('text')}>
                            <i className="fi fi-rr-text"></i> Text
                        </div>
                        <div className={linkadmin['dropdown-item']} onClick={() => handleAddNew('link')}>
                            <i className="fi fi-rr-link"></i> Link
                        </div>
                    </div>
                )}
            </div>

            {isEditModalOpen && (
                <div className={linkadmin['modal']}>
                    <div className={linkadmin['modal-content']}>
                        <h3>Edit {currentItem?.type === 'link' ? 'Link' : 'Text'}</h3>
                        <div className={linkadmin['form-group']}>
                            <label>Title</label>
                            <input
                                type="text"
                                value={currentItem?.title || ''}
                                onChange={(e) => setCurrentItem({...currentItem, title: e.target.value})}
                            />
                        </div>
                        <div className={linkadmin['form-group']}>
                            <label>Content</label>
                            <textarea
                                value={currentItem?.content || ''}
                                onChange={(e) => setCurrentItem({...currentItem, content: e.target.value})}
                            />
                        </div>
                        <div className={linkadmin['modal-actions']}>
                            <button className={linkadmin['square-button']} onClick={handleSave} title="Save">
                                <i className="fi fi-rr-check"></i>
                            </button>
                            <button className={linkadmin['square-button']} onClick={() => setIsEditModalOpen(false)}
                                    title="Cancel">
                                <i className="fi fi-rr-cross"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isNewModalOpen && (
                <div className={linkadmin['modal']}>
                    <div className={linkadmin['modal-content']}>
                        <h3>Add {currentItem?.type === 'link' ? 'Link' : 'Text'}</h3>
                        <div className={linkadmin['form-group']}>
                            <label>Title</label>
                            <input
                                type="text"
                                value={currentItem?.title || ''}
                                onChange={(e) => setCurrentItem({...currentItem, title: e.target.value})}
                                required
                            />
                        </div>
                        <div className={linkadmin['form-group']}>
                            <label>Content</label>
                            <textarea
                                value={currentItem?.content || ''}
                                onChange={(e) => setCurrentItem({...currentItem, content: e.target.value})}
                                required
                            />
                        </div>
                        <div className={linkadmin['modal-actions']}>
                            <button className={linkadmin['square-button']} onClick={handleSave} title="Save">
                                <i className="fi fi-rr-check"></i>
                            </button>
                            <button className={linkadmin['square-button']} onClick={() => setIsNewModalOpen(false)}
                                    title="Cancel">
                                <i className="fi fi-rr-cross"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isTitleModalOpen && (
                <div className={linkadmin['modal']}>
                    <div className={linkadmin['modal-content']}>
                        <h3>Edit Page Title</h3>
                        <div className={linkadmin['form-group']}>
                            <label>Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>
                        <div className={linkadmin['modal-actions']}>
                            <button className={linkadmin['square-button']} onClick={handleTitleSave} title="Save">
                                <i className="fi fi-rr-check"></i>
                            </button>
                            <button className={linkadmin['square-button']} onClick={() => setIsTitleModalOpen(false)}
                                    title="Cancel">
                                <i className="fi fi-rr-cross"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LinkAdmin;